import { getApiClient, NO_BACKOFF } from '@littlebird/sdk';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';
import { trackEvent } from '@/utils/analytics';

import { type SpriteType, spriteTypeOf } from './sprite-presentation';

export interface SpriteSummary {
  id: string;
  name: string;
  /** The saved file this sprite renders, or null for a legacy html sprite / deleted file. */
  file_id: string | null;
  /** Rendered content MIME type; drives the list icon/thumbnail ("text/html" for legacy sprites). */
  content_type: string | null;
  source_chat_id: string | null;
  source_chat_title: string | null;
  thumbnail_url: string | null;
  updated_at: string;
}

export type SpriteViewMode = 'grid' | 'list';

export interface SpriteDetail extends SpriteSummary {
  /** Legacy inline HTML; null for file-reference sprites. */
  html: string | null;
}

export const fetchSprite = (spriteId: string): Promise<SpriteDetail> =>
  getApiClient().fetch<SpriteDetail>(`sprites/${spriteId}`, {
    method: 'GET',
  });

export const spriteArtifactType = (name: string): string => {
  const dotIndex = name.lastIndexOf('.');
  return dotIndex === -1 ? 'none' : name.slice(dotIndex + 1).toLowerCase();
};

/**
 * In-memory list of the user's saved sprites. Plain fetch-on-demand (no sync
 * engine): the list page calls `refresh()` on every visit so newly created
 * artifacts show up, and save/delete update the list locally so the UI reflects
 * changes immediately.
 */
class SpritesStore {
  sprites: SpriteSummary[] = [];
  isLoaded = false;
  viewMode: SpriteViewMode = 'grid';
  searchQuery = '';
  typeFilters: SpriteType[] = [];
  searchOpen = false;
  private pendingRefresh: Promise<void> | null = null;
  private lookedUpFileIds = new Set<string>();

  constructor() {
    makeObservable(this, {
      sprites: observable,
      isLoaded: observable,
      viewMode: observable,
      searchQuery: observable,
      typeFilters: observable,
      visibleSprites: computed,
      setViewMode: action,
      setSearchQuery: action,
      toggleTypeFilter: action,
      clearTypeFilters: action,
      showOnlyType: action,
      searchOpen: observable,
      setSearchOpen: action,
      applyThumbnail: action,
    });

    makePersistable(this, {
      name: 'spritesStore',
      properties: ['viewMode'],
      storage: window.localStorage,
    });
  }

  get visibleSprites(): SpriteSummary[] {
    const query = this.searchQuery.trim().toLowerCase();
    return this.sprites.filter((sprite) => {
      if (
        this.typeFilters.length > 0 &&
        !this.typeFilters.includes(
          spriteTypeOf(sprite.content_type, sprite.name)
        )
      )
        return false;
      if (!query) return true;
      return `${sprite.name} ${sprite.source_chat_title ?? ''}`
        .toLowerCase()
        .includes(query);
    });
  }

  setViewMode(mode: SpriteViewMode) {
    this.viewMode = mode;
  }
  setSearchQuery(query: string) {
    this.searchQuery = query;
  }
  toggleTypeFilter(type: SpriteType) {
    this.typeFilters = this.typeFilters.includes(type)
      ? this.typeFilters.filter((current) => current !== type)
      : [...this.typeFilters, type];
  }
  clearTypeFilters() {
    this.typeFilters = [];
  }

  setSearchOpen(open: boolean) {
    this.searchOpen = open;
    if (!open) this.searchQuery = '';
  }

  showOnlyType(type: SpriteType) {
    this.typeFilters = [type];
  }

  applyThumbnail(fileId: string, thumbnailUrl: string) {
    for (const sprite of this.sprites) {
      if (sprite.file_id === fileId) sprite.thumbnail_url = thumbnailUrl;
    }
  }

  refresh(): Promise<void> {
    this.pendingRefresh ??= this.fetchSprites().finally(() => {
      this.pendingRefresh = null;
    });
    return this.pendingRefresh;
  }

  async loadSpriteFor(fileId: string | undefined): Promise<void> {
    if (
      !fileId ||
      this.lookedUpFileIds.has(fileId) ||
      this.sprites.some((sprite) => sprite.file_id === fileId)
    )
      return;
    await this.refresh();
    this.lookedUpFileIds.add(fileId);
  }

  private async fetchSprites(): Promise<void> {
    const sprites = await getApiClient().fetch<SpriteSummary[]>('sprites', {
      method: 'GET',
    });
    runInAction(() => {
      this.sprites = sprites;
      this.isLoaded = true;
    });
  }

  /** Create or overwrite-by-name (server-side); keeps the list in sync. */
  async save(
    name: string,
    fileId: string,
    sourceChatId?: string
  ): Promise<SpriteSummary> {
    const saved = await getApiClient().fetch<SpriteSummary>('sprites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        file_id: fileId,
        source_chat_id: sourceChatId,
      }),
      backoff: NO_BACKOFF,
    });
    runInAction(() => {
      this.sprites = [
        saved,
        ...this.sprites.filter((sprite) => sprite.id !== saved.id),
      ];
      this.isLoaded = true;
    });
    return saved;
  }

  async remove(spriteId: string): Promise<void> {
    await getApiClient().fetch(`sprites/${spriteId}`, {
      method: 'DELETE',
      backoff: NO_BACKOFF,
    });
    runInAction(() => {
      this.sprites = this.sprites.filter((sprite) => sprite.id !== spriteId);
    });
  }
}

export const spritesStore = new SpritesStore();

export const deleteSpriteWithFeedback = async (
  sprite: Pick<SpriteSummary, 'id' | 'name'>,
  source: 'list' | 'detail',
  context: string
): Promise<boolean> => {
  try {
    await spritesStore.remove(sprite.id);
    trackEvent('SPRITE_DELETED', {
      sprite_id: sprite.id,
      artifact_type: spriteArtifactType(sprite.name),
      source,
    });
    toast.success(`Deleted "${sprite.name}"`);
    return true;
  } catch (error) {
    logError(error, 'Failed to delete sprite', {
      context,
      metadata: { spriteId: sprite.id },
    });
    toast.error('Failed to delete artifact');
    return false;
  }
};
